<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import differenceInDays from 'date-fns/differenceInDays';
import ActiveFilterChip from './Filters/v3/ActiveFilterChip.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { GROUP_BY_FILTER } from '../constants';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import {
  readReportFilters,
  restoredDateRange,
  writeReportFilters,
} from '../helpers/reportFilterStorage';

const props = defineProps({
  filterType: {
    type: String,
    required: false,
    default: '',
    validator: value =>
      ['teams', 'inboxes', 'labels', 'agents', ''].includes(value),
  },
  selectedItem: {
    type: Object,
    default: null,
  },
  showGroupBy: {
    type: Boolean,
    default: true,
  },
  showBusinessHours: {
    type: Boolean,
    default: true,
  },
  showEntityFilter: {
    type: Boolean,
    default: true,
  },
  showInboxFilter: {
    type: Boolean,
    default: false,
  },
  showAgentFilter: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();

// The chips that narrow the report by a set of inboxes or agents, rather than replacing the single
// dimension the entity filter above navigates to.
const SCOPE_INBOXES = 'scopeInboxes';
const SCOPE_AGENTS = 'scopeAgents';

const buildReportFilterList = (items, type) => {
  if (!Array.isArray(items)) return [];

  return items.map(item => ({
    id: item.id,
    name: item.name || item.title,
    type,
  }));
};

const getReportFilterKey = filterType => {
  const keyMap = {
    teams: 'team_id',
    inboxes: 'inbox_id',
    labels: 'label_id',
    agents: 'agent_id',
  };
  return keyMap[filterType] || '';
};

const getFilterKey = () => getReportFilterKey(props.filterType);

const showSubDropdownMenu = ref(false);
const showGroupByDropdown = ref(false);
const activeFilterType = ref('');
const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const businessHoursSelected = ref(false);
const groupBy = ref(GROUP_BY_FILTER[1]);
const groupByfilterItemsList = ref([{ id: 1, name: 'Day' }]);
const selectedInboxIds = ref([]);
const selectedAgentIds = ref([]);
// A scope selection is applied when the dropdown closes: picking three inboxes is one gesture, and
// refetching the whole panel after each tick would be three rounds of the same eight requests.
const hasPendingScopeChange = ref(false);

const appliedFilters = ref(
  props.showEntityFilter
    ? { [getFilterKey()]: props.selectedItem?.id || null }
    : {}
);

const filterSource = computed(() => {
  const sources = {
    teams: store.getters['teams/getTeams'],
    inboxes: store.getters['inboxes/getInboxes'],
    labels: store.getters['labels/getLabels'],
    agents: store.getters['agents/getAgents'],
  };
  return sources[props.filterType] || [];
});

const inboxOptions = computed(() =>
  buildReportFilterList(store.getters['inboxes/getInboxes'], SCOPE_INBOXES)
);

const agentOptions = computed(() =>
  buildReportFilterList(store.getters['agents/getAgents'], SCOPE_AGENTS)
);

const from = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const to = computed(() => getUnixEndOfDay(customDateRange.value[1]));

const daysDifference = computed(() => {
  return differenceInDays(customDateRange.value[1], customDateRange.value[0]);
});

const isGroupByPossible = computed(() => {
  return props.showGroupBy && daysDifference.value >= 29;
});

const GROUP_BY_OPTIONS = computed(() => ({
  WEEK: [
    { id: 1, name: t('REPORT.GROUPING_OPTIONS.DAY') },
    { id: 2, name: t('REPORT.GROUPING_OPTIONS.WEEK') },
  ],
  MONTH: [
    { id: 1, name: t('REPORT.GROUPING_OPTIONS.DAY') },
    { id: 2, name: t('REPORT.GROUPING_OPTIONS.WEEK') },
    { id: 3, name: t('REPORT.GROUPING_OPTIONS.MONTH') },
  ],
  YEAR: [
    { id: 2, name: t('REPORT.GROUPING_OPTIONS.WEEK') },
    { id: 3, name: t('REPORT.GROUPING_OPTIONS.MONTH') },
    { id: 4, name: t('REPORT.GROUPING_OPTIONS.YEAR') },
  ],
}));

const fetchFilterItems = () => {
  const days = daysDifference.value;
  if (days >= 364) return GROUP_BY_OPTIONS.value.YEAR;
  if (days >= 90) return GROUP_BY_OPTIONS.value.MONTH;
  if (days >= 29) return GROUP_BY_OPTIONS.value.WEEK;
  return GROUP_BY_OPTIONS.value.WEEK;
};

const filterOptions = computed(() =>
  buildReportFilterList(filterSource.value, props.filterType)
);

const filterPlaceholder = computed(() => {
  const placeholders = {
    teams: 'TEAM_REPORTS.FILTERS.INPUT_PLACEHOLDER.TEAMS',
    inboxes: 'INBOX_REPORTS.FILTERS.INPUT_PLACEHOLDER.INBOXES',
    labels: 'LABEL_REPORTS.FILTERS.INPUT_PLACEHOLDER.LABELS',
    agents: 'AGENT_REPORTS.FILTERS.INPUT_PLACEHOLDER.AGENTS',
  };
  return t(placeholders[props.filterType] || '');
});

const defaultFilterLabel = computed(() => {
  const labelKeys = {
    teams: 'TEAM_REPORTS.FILTER_DROPDOWN_LABEL',
    inboxes: 'INBOX_REPORTS.FILTER_DROPDOWN_LABEL',
    labels: 'LABEL_REPORTS.FILTER_DROPDOWN_LABEL',
    agents: 'AGENT_REPORTS.FILTER_DROPDOWN_LABEL',
  };
  return t(labelKeys[props.filterType] || 'FORMS.MULTISELECT.SELECT_ONE');
});

const selectedFilterName = computed(() => {
  const filterKey = getFilterKey();
  const selectedId = appliedFilters.value[filterKey];

  if (!selectedId) {
    return defaultFilterLabel.value;
  }

  const selectedItem = filterOptions.value.find(item => item.id === selectedId);
  return selectedItem?.name || defaultFilterLabel.value;
});

// One name reads better than "1 inbox"; past that the chip only has room for the count.
const singleSelectionName = (selectedIds, options) =>
  selectedIds.length === 1 &&
  options.find(option => option.id === selectedIds[0])?.name;

const inboxScopeLabel = computed(() => {
  const selectedIds = selectedInboxIds.value;
  if (!selectedIds.length) return t('REPORT.INBOX_SCOPE.ALL');
  return (
    singleSelectionName(selectedIds, inboxOptions.value) ||
    t('REPORT.INBOX_SCOPE.SELECTED', { count: selectedIds.length })
  );
});

const agentScopeLabel = computed(() => {
  const selectedIds = selectedAgentIds.value;
  if (!selectedIds.length) return t('REPORT.AGENT_SCOPE.ALL');
  return (
    singleSelectionName(selectedIds, agentOptions.value) ||
    t('REPORT.AGENT_SCOPE.SELECTED', { count: selectedIds.length })
  );
});

// Only the filters this screen actually shows are written back, so a report without the scope chips
// does not clear the inboxes the operations panel is filtered by.
const persistFilters = () => {
  const filters = {
    from: from.value,
    to: to.value,
    range: selectedDateRange.value,
    businessHours: businessHoursSelected.value,
    groupBy: isGroupByPossible.value ? groupBy.value.id : null,
  };

  if (props.showInboxFilter) filters.inboxIds = selectedInboxIds.value;
  if (props.showAgentFilter) filters.agentIds = selectedAgentIds.value;

  writeReportFilters(accountId.value, filters);
};

const emitChange = () => {
  const payload = {
    from: from.value,
    to: to.value,
    businessHours: businessHoursSelected.value,
    inboxIds: selectedInboxIds.value,
    agentIds: selectedAgentIds.value,
  };

  if (props.showGroupBy) {
    // Always emit groupBy, default to day when range is too short
    payload.groupBy = isGroupByPossible.value
      ? groupBy.value
      : GROUP_BY_FILTER[1];
  }

  if (props.showEntityFilter) {
    const filterKey = getFilterKey();
    const selectedValue = appliedFilters.value[filterKey];

    if (selectedValue) {
      payload[props.filterType] =
        props.filterType === 'agents'
          ? [{ id: selectedValue }]
          : { id: selectedValue };
    }
  }

  hasPendingScopeChange.value = false;
  persistFilters();
  emit('filterChange', payload);
};

const flushScopeChange = () => {
  if (hasPendingScopeChange.value) emitChange();
};

const closeActiveFilterDropdown = () => {
  showSubDropdownMenu.value = false;
  activeFilterType.value = '';
  flushScopeChange();
};

const openActiveFilterDropdown = filterType => {
  showGroupByDropdown.value = false;
  if (activeFilterType.value !== filterType) {
    flushScopeChange();
    activeFilterType.value = filterType;
    showSubDropdownMenu.value = true;
    return;
  }
  showSubDropdownMenu.value = !showSubDropdownMenu.value;
  if (!showSubDropdownMenu.value) flushScopeChange();
};

const addFilter = item => {
  const filterKey = getFilterKey();
  appliedFilters.value[filterKey] = item.id;
  closeActiveFilterDropdown();
  emitChange();

  // Navigate to the new entity's route
  const routeNameMap = {
    teams: 'team_reports_show',
    inboxes: 'inbox_reports_show',
    labels: 'label_reports_show',
    agents: 'agent_reports_show',
  };

  const routeName = routeNameMap[props.filterType];
  if (routeName) {
    router.push({
      name: routeName,
      params: { ...route.params, id: item.id },
    });
  }
};

const toggleId = (ids, id) =>
  ids.includes(id) ? ids.filter(current => current !== id) : [...ids, id];

const toggleInboxScope = item => {
  selectedInboxIds.value = toggleId(selectedInboxIds.value, item.id);
  hasPendingScopeChange.value = true;
};

const toggleAgentScope = item => {
  selectedAgentIds.value = toggleId(selectedAgentIds.value, item.id);
  hasPendingScopeChange.value = true;
};

const clearInboxScope = () => {
  selectedInboxIds.value = [];
  hasPendingScopeChange.value = true;
  closeActiveFilterDropdown();
};

const clearAgentScope = () => {
  selectedAgentIds.value = [];
  hasPendingScopeChange.value = true;
  closeActiveFilterDropdown();
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  groupByfilterItemsList.value = fetchFilterItems();
  const filterItems = groupByfilterItemsList.value.filter(
    item => item.id === groupBy.value.id
  );
  if (filterItems.length === 0) {
    groupBy.value = GROUP_BY_FILTER[groupByfilterItemsList.value[0].id];
  }
  emitChange();
};

const onBusinessHoursToggle = () => {
  emitChange();
};

const onGroupByFilterChange = payload => {
  groupBy.value = GROUP_BY_FILTER[payload.id];
  showGroupByDropdown.value = false;
  emitChange();
};

const toggleGroupByDropdown = () => {
  showGroupByDropdown.value = !showGroupByDropdown.value;
};

const closeGroupByDropdown = () => {
  showGroupByDropdown.value = false;
};

const restoreFilters = () => {
  const stored = readReportFilters(accountId.value);

  if (stored.range) {
    selectedDateRange.value = stored.range;
  }

  const dateRange = restoredDateRange(stored);
  if (dateRange) customDateRange.value = dateRange;

  if (stored.businessHours) {
    businessHoursSelected.value = stored.businessHours;
  }

  if (stored.groupBy) {
    const groupByValue = GROUP_BY_FILTER[stored.groupBy];
    if (groupByValue) {
      groupBy.value = groupByValue;
    }
  }

  if (props.showInboxFilter && stored.inboxIds) {
    selectedInboxIds.value = stored.inboxIds;
  }

  if (props.showAgentFilter && stored.agentIds) {
    selectedAgentIds.value = stored.agentIds;
  }

  // Initialize entity filter from route params
  if (props.showEntityFilter && route.params.id) {
    const filterKey = getFilterKey();
    appliedFilters.value[filterKey] = Number(route.params.id);
  }
};

onMounted(() => {
  // Filters used to live in the query string; a bookmark from back then would otherwise sit there
  // saying something the screen no longer reads.
  if (Object.keys(route.query).length) router.replace({ query: {} });
  restoreFilters();
  groupByfilterItemsList.value = fetchFilterItems();
  emitChange();
});
</script>

<template>
  <div class="flex flex-col w-full gap-3 lg:flex-row">
    <WootDatePicker
      v-model:date-range="customDateRange"
      v-model:range-type="selectedDateRange"
      @date-range-changed="onDateRangeChange"
    />

    <div class="flex gap-2 items-center w-full">
      <ActiveFilterChip
        v-if="showEntityFilter"
        :id="appliedFilters[getFilterKey()]"
        :name="selectedFilterName"
        :type="filterType"
        :options="filterOptions"
        :active-filter-type="activeFilterType"
        :show-menu="showSubDropdownMenu"
        :placeholder="filterPlaceholder"
        :show-clear-filter="false"
        enable-search
        @toggle-dropdown="openActiveFilterDropdown"
        @close-dropdown="closeActiveFilterDropdown"
        @add-filter="addFilter"
      />

      <ActiveFilterChip
        v-if="showInboxFilter"
        :name="inboxScopeLabel"
        :selected-ids="selectedInboxIds"
        :type="SCOPE_INBOXES"
        :options="inboxOptions"
        :active-filter-type="activeFilterType"
        :show-menu="showSubDropdownMenu"
        :placeholder="$t('REPORT.INBOX_SCOPE.PLACEHOLDER')"
        enable-search
        show-clear-filter
        @toggle-dropdown="openActiveFilterDropdown"
        @close-dropdown="closeActiveFilterDropdown"
        @add-filter="toggleInboxScope"
        @remove-filter="clearInboxScope"
      />

      <ActiveFilterChip
        v-if="showAgentFilter"
        :name="agentScopeLabel"
        :selected-ids="selectedAgentIds"
        :type="SCOPE_AGENTS"
        :options="agentOptions"
        :active-filter-type="activeFilterType"
        :show-menu="showSubDropdownMenu"
        :placeholder="$t('REPORT.AGENT_SCOPE.PLACEHOLDER')"
        enable-search
        show-clear-filter
        @toggle-dropdown="openActiveFilterDropdown"
        @close-dropdown="closeActiveFilterDropdown"
        @add-filter="toggleAgentScope"
        @remove-filter="clearAgentScope"
      />

      <ActiveFilterChip
        v-if="isGroupByPossible"
        :id="groupBy?.id"
        :name="
          groupByfilterItemsList.find(item => item.id === groupBy?.id)?.name ||
          $t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL')
        "
        type="groupBy"
        :options="groupByfilterItemsList"
        :active-filter-type="showGroupByDropdown ? 'groupBy' : ''"
        :show-menu="showGroupByDropdown"
        :placeholder="$t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL')"
        :enable-search="false"
        :show-clear-filter="false"
        @toggle-dropdown="toggleGroupByDropdown"
        @close-dropdown="closeGroupByDropdown"
        @add-filter="onGroupByFilterChange"
        @remove-filter="() => {}"
      />

      <div
        v-if="showBusinessHours"
        class="flex items-center flex-shrink-0 ltr:ml-auto rtl:mr-auto"
      >
        <span class="mx-2 text-sm whitespace-nowrap">
          {{ $t('REPORT.BUSINESS_HOURS') }}
        </span>
        <span>
          <ToggleSwitch
            v-model="businessHoursSelected"
            @change="onBusinessHoursToggle"
          />
        </span>
      </div>
    </div>
  </div>
</template>
